/**
 * Email Report Button Component
 * Floating button that appears when users have saved card actions
 * Allows users to send their checklist as an email report
 */

import { CardAction, CardActionData } from '../types';
import { StorageService } from '../services';
import { EMAIL_REPORT_COLORS } from '../design-system/colors';

/**
 * Email icon SVG
 */
const EMAIL_ICON = `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
  <path stroke-linecap="round" stroke-linejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
</svg>`;

/**
 * Trash icon SVG for clear button
 */
const TRASH_ICON = `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
  <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
</svg>`;

/**
 * Callback type for when feedback is cleared
 */
export type OnFeedbackClearedCallback = () => void;

/**
 * Component for the floating email report button
 */
export class EmailReportButtonComponent {
  private storageService: StorageService;
  private containerElement: HTMLElement | null = null;
  private auditName: string = '';
  private onFeedbackClearedCallbacks: OnFeedbackClearedCallback[] = [];

  constructor(storageService?: StorageService) {
    this.storageService = storageService || new StorageService();
  }

  /**
   * Register a callback for when feedback is cleared
   * @param callback - Function to call when feedback is cleared
   */
  onFeedbackCleared(callback: OnFeedbackClearedCallback): void {
    this.onFeedbackClearedCallbacks.push(callback);
  }

  /**
   * Set the audit name for the email subject
   * @param auditName - Name of the current audit
   */
  setAuditName(auditName: string): void {
    this.auditName = auditName;
  }

  /**
   * Create and render the email report button
   * @returns The container element
   */
  render(): HTMLElement {
    // Remove existing container if present
    this.remove();

    // Create container
    this.containerElement = document.createElement('div');
    this.containerElement.id = 'email-report-container';
    this.containerElement.className = `fixed bottom-6 right-6 z-50 transition-all duration-300`;

    this.updateContent();

    return this.containerElement;
  }

  /**
   * Update the button content based on current action count
   */
  updateContent(): void {
    if (!this.containerElement) return;

    const actionCount = this.storageService.getActionCount();

    if (actionCount === 0) {
      this.containerElement.innerHTML = '';
      this.containerElement.classList.add('opacity-0', 'pointer-events-none');
      return;
    }

    this.containerElement.classList.remove('opacity-0', 'pointer-events-none');

    this.containerElement.innerHTML = `
      <div class="flex flex-col items-end gap-2">
        <div class="${EMAIL_REPORT_COLORS.container.background} ${EMAIL_REPORT_COLORS.container.border} border rounded-lg px-3 py-2 text-sm text-gray-300 backdrop-blur-sm">
          ${actionCount} item${actionCount !== 1 ? 's' : ''} marked
        </div>
        <div class="flex items-center gap-2">
          <button
            id="clear-feedback-btn"
            class="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-red-900/50 text-gray-300 hover:text-red-400 border border-gray-600 hover:border-red-500 rounded-lg text-sm font-medium transition-all duration-200"
            aria-label="Clear all feedback"
            title="Clear Feedback"
          >
            ${TRASH_ICON}
            <span>Clear</span>
          </button>
          <button
            id="send-report-btn"
            class="flex items-center gap-2 px-4 py-2 ${EMAIL_REPORT_COLORS.button.background} ${EMAIL_REPORT_COLORS.button.text} ${EMAIL_REPORT_COLORS.button.shadow} rounded-lg font-medium transition-all duration-200 hover:scale-105 active:scale-95"
            aria-label="Send report email with ${actionCount} items"
          >
            ${EMAIL_ICON}
            <span>Send Report</span>
          </button>
        </div>
      </div>
    `;

    this.attachHandlers();
  }

  /**
   * Attach click handlers to the buttons
   */
  private attachHandlers(): void {
    const sendButton = document.getElementById('send-report-btn');
    if (sendButton) {
      sendButton.addEventListener('click', () => this.sendReportEmail());
    }

    const clearButton = document.getElementById('clear-feedback-btn');
    if (clearButton) {
      clearButton.addEventListener('click', () => this.clearFeedback());
    }
  }

  /**
   * Clear all saved feedback and update UI
   */
  private clearFeedback(): void {
    // Confirm before clearing
    const actionCount = this.storageService.getActionCount();
    const confirmed = window.confirm(
      `Clear all ${actionCount} feedback item${actionCount !== 1 ? 's' : ''}? This cannot be undone.`
    );

    if (!confirmed) return;

    // Clear storage
    this.storageService.clearActions();

    // Notify callbacks so card UI can be updated
    this.onFeedbackClearedCallbacks.forEach(callback => callback());

    // Update this component's UI
    this.updateContent();
  }

  /**
   * Generate and send the email report
   */
  private sendReportEmail(): void {
    const actions = this.storageService.getAllActionsArray();

    if (actions.length === 0) {
      return;
    }

    const emailBody = this.generateEmailBody(actions);
    const subject = this.auditName
      ? `Actionbase Audit Report - ${this.auditName}`
      : 'Actionbase Audit Report - My Action Checklist';

    const mailtoLink = `mailto:vincent@actionbase.ai?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`;

    window.location.href = mailtoLink;
  }

  /**
   * Generate the email body content
   * @param actions - Array of card action data
   * @returns Formatted email body
   */
  private generateEmailBody(actions: CardActionData[]): string {
    const grouped = this.groupActionsByType(actions);

    let body = 'MY ACTIONBASE AUDIT CHECKLIST\n';
    body += '='.repeat(50) + '\n\n';

    if (this.auditName) {
      body += `Audit: ${this.auditName}\n`;
      body += `Date: ${new Date().toLocaleDateString()}\n\n`;
    }

    // Summary counts
    body += 'SUMMARY\n';
    body += '-'.repeat(30) + '\n';
    body += `Total items reviewed: ${actions.length}\n`;
    if (grouped['done'].length > 0) body += `✓ Done: ${grouped['done'].length}\n`;
    if (grouped['thumbs-up'].length > 0) body += `👍 Good recommendations: ${grouped['thumbs-up'].length}\n`;
    if (grouped['thumbs-down'].length > 0) body += `👎 Not helpful: ${grouped['thumbs-down'].length}\n`;
    if (grouped['not-applicable'].length > 0) body += `⊘ Not applicable: ${grouped['not-applicable'].length}\n`;
    body += '\n';

    // Done items
    if (grouped['done'].length > 0) {
      body += '\nCOMPLETED ITEMS\n';
      body += '-'.repeat(30) + '\n';
      grouped['done'].forEach(action => {
        body += `✓ [${action.area}] ${action.recommendation}\n`;
      });
    }

    // Not applicable items
    if (grouped['not-applicable'].length > 0) {
      body += '\nNOT APPLICABLE\n';
      body += '-'.repeat(30) + '\n';
      grouped['not-applicable'].forEach(action => {
        body += `⊘ [${action.area}] ${action.recommendation}\n`;
      });
    }

    // Good recommendations
    if (grouped['thumbs-up'].length > 0) {
      body += '\nGOOD RECOMMENDATIONS\n';
      body += '-'.repeat(30) + '\n';
      grouped['thumbs-up'].forEach(action => {
        body += `👍 [${action.area}] ${action.recommendation}\n`;
      });
    }

    // Not helpful
    if (grouped['thumbs-down'].length > 0) {
      body += '\nNOT HELPFUL\n';
      body += '-'.repeat(30) + '\n';
      grouped['thumbs-down'].forEach(action => {
        body += `👎 [${action.area}] ${action.recommendation}\n`;
      });
    }

    body += '\n' + '='.repeat(50) + '\n';
    body += 'Generated by Actionbase CRO Audit System\n';

    return body;
  }

  /**
   * Group actions by their action type
   * @param actions - Array of card action data
   * @returns Actions grouped by type
   */
  private groupActionsByType(actions: CardActionData[]): Record<CardAction, CardActionData[]> {
    const grouped: Record<CardAction, CardActionData[]> = {
      'thumbs-up': [],
      'thumbs-down': [],
      'done': [],
      'not-applicable': []
    };

    actions.forEach(action => {
      if (grouped[action.action]) {
        grouped[action.action].push(action);
      }
    });

    return grouped;
  }

  /**
   * Update visibility based on whether actions exist
   * @param hasActions - Whether any actions have been saved
   */
  updateVisibility(hasActions: boolean): void {
    if (hasActions) {
      this.updateContent();
    } else if (this.containerElement) {
      this.containerElement.classList.add('opacity-0', 'pointer-events-none');
    }
  }

  /**
   * Remove the button from the DOM
   */
  remove(): void {
    if (this.containerElement) {
      this.containerElement.remove();
      this.containerElement = null;
    }
  }

  /**
   * Get the action count
   * @returns Number of saved actions
   */
  getActionCount(): number {
    return this.storageService.getActionCount();
  }
}
